import type { FC } from 'react';
import type { ITableData } from '../../pages/dashboard-search/types';

import { useState, memo } from 'react';
import { Table } from 'flowbite-react';

import { Pagination } from 'antd';
import {
  HiChevronDoubleDown,
  HiChevronDoubleUp,
  HiOutlineSelector,
} from 'react-icons/hi';

const ChartTable: FC<ITableData> = ({ tableHeaders, tableData }) => {
  const [currentPage, setCurrentPage] = useState(1);

  enum SortConfigTypeEnum {
    disabled = 'disabled',
    asc = 'asc',
    desc = 'desc',
  }

  const nextSort = (sortValue: SortConfigTypeEnum): SortConfigTypeEnum => {
    const enumValues = Object.values(SortConfigTypeEnum);
    const index = enumValues.findIndex((val) => val === sortValue);

    return (enumValues?.[index + 1] ?? enumValues[0]) as SortConfigTypeEnum;
  };

  const [sortConfig, setSortConfig] = useState<SortConfigTypeEnum[]>(
    tableHeaders.map(() => SortConfigTypeEnum.disabled)
  );

  const sortedTableData = [...tableData].sort((a, b) => {
    const sortedIndex = sortConfig.findIndex(
      (sortEl) => sortEl !== SortConfigTypeEnum.disabled
    );

    const sortedConfigElem = sortConfig?.[sortedIndex];
    if (!sortedConfigElem) return 0;

    if (sortedConfigElem === SortConfigTypeEnum.desc)
      return (a[sortedIndex] as number) - (b[sortedIndex] as number);

    return (b[sortedIndex] as number) - (a[sortedIndex] as number);
  });

  const currentPageData = sortedTableData.slice(
    (currentPage - 1) * 10,
    currentPage * 10
  );

  const onClickSortButton = (index: number) => {
    setSortConfig((prev) =>
      prev.map((el, configIndex) => {
        if (index === configIndex) {
          return nextSort(el);
        }
        return SortConfigTypeEnum.disabled;
      })
    );
  };

  const iconsMap = {
    [SortConfigTypeEnum.disabled]: <HiOutlineSelector size={16} />,
    [SortConfigTypeEnum.asc]: <HiChevronDoubleUp size={12} />,
    [SortConfigTypeEnum.desc]: <HiChevronDoubleDown size={12} />,
  };

  return (
    <div className="absolute left-0 top-0 flex h-full w-full flex-col bg-gray-50 dark:bg-gray-700">
      <Table
        theme={{
          root: {
            wrapper: 'overflow-auto h-full',
          },
        }}
      >
        <Table.Head>
          {tableHeaders.map((title, index) => (
            <Table.HeadCell key={title}>
              <div
                className={
                  'flex items-center ' +
                  (index > 0 ? 'text-right justify-end' : '')
                }
              >
                <div>{title}</div>
                {typeof tableData?.[0]?.[index] === 'number' && (
                  <button
                    className="ml-1 h-[24px] w-[24px]"
                    onClick={() => onClickSortButton(index)}
                  >
                    {iconsMap?.[sortConfig?.[index] as SortConfigTypeEnum]}
                  </button>
                )}
              </div>
            </Table.HeadCell>
          ))}
        </Table.Head>
        <Table.Body className="divide-y">
          {currentPageData?.length
            ? currentPageData.map((dataRow, index) => (
                <Table.Row key={index}>
                  {dataRow.map((dataValue, index) => (
                    <Table.Cell
                      className={index > 0 ? 'text-right' : ''}
                      key={index}
                    >
                      {+dataValue ? +(+dataValue)?.toFixed?.(1) : dataValue}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            : null}
        </Table.Body>
      </Table>
      <div className="flex items-center justify-between p-2 text-sm text-gray-500">
        <span className="px-2">
          Showing{' '}
          <span className="font-bold text-gray-900 dark:text-gray-300">
            {currentPage * 10 - 9}-{currentPage * 10}
          </span>{' '}
          of {tableData.length}
        </span>
        <Pagination
          simple
          defaultCurrent={currentPage}
          onChange={setCurrentPage}
          total={tableData.length}
        />
      </div>
    </div>
  );
};

export default memo(ChartTable);
